package org.catharsis.spendtel.feature.data

import kotlinx.serialization.Serializable

@Serializable
data class BillingEntitlement(
    val canCreateReport: Boolean,
    val reportsCreated: Int,
    val paidCredits: Int,
    val freeReportAllowance: Int,
    val freeReportsRemaining: Int,
    val needsPayment: Boolean,
    val priceLabel: String,
)

@Serializable
data class CheckoutSessionRequest(
    val successPath: String? = null,
    val cancelPath: String? = null,
)

@Serializable
data class CheckoutSessionResponse(
    val checkoutUrl: String,
)

@Serializable
data class PaymentRequiredError(
    val code: String,
    val message: String,
    val priceLabel: String,
    val checkoutUrl: String? = null,
)

const val DEFAULT_REPORT_PRICE_LABEL = "\$4.99"

fun normalizePriceLabel(raw: String?): String {
    val label = raw?.trim().orEmpty()
    return when {
        label == DEFAULT_REPORT_PRICE_LABEL -> label
        label == ".99" || label == "$.99" -> DEFAULT_REPORT_PRICE_LABEL
        label.startsWith('$') && label.length > 1 -> label
        label.matches(Regex("""\d+\.\d{2}""")) -> "$$label"
        label.isNotEmpty() -> label
        else -> DEFAULT_REPORT_PRICE_LABEL
    }
}

fun friendlyPurchaseError(raw: String?): String {
    val message = raw?.trim().orEmpty()
    if (message.isEmpty()) {
        return "Purchase couldn't be completed. Please try again."
    }

    return when {
        message.contains("cancel", ignoreCase = true) ->
            "Purchase was cancelled."
        message.contains("network", ignoreCase = true) ||
            message.contains("internet", ignoreCase = true) ->
            "Check your connection and try again."
        message.contains("processing", ignoreCase = true) ->
            "Payment is still processing. Wait a moment, then try again."
        message.contains("not configured", ignoreCase = true) ||
            message.contains("offering", ignoreCase = true) ||
            message.contains("product", ignoreCase = true) ->
            "Purchases aren't available right now. Please try again later."
        message.length > 120 ->
            "Purchase couldn't be completed. Please try again."
        else -> message
    }
}

fun normalizePaywallMessage(message: String, priceLabel: String): String {
    val normalizedPrice = normalizePriceLabel(priceLabel)
    return when {
        message.contains("for .99") ->
            message.replace("for .99", "for $normalizedPrice")
        message.contains("for $.99") ->
            message.replace("for $.99", "for $normalizedPrice")
        else -> message
    }
}

fun BillingEntitlement.entitlementHint(): String? {
    val price = normalizePriceLabel(priceLabel)
    return when {
        freeReportsRemaining > 0 && reportsCreated == 0 ->
            "Your first spending story is free."
        freeReportsRemaining > 0 ->
            "$freeReportsRemaining free report${if (freeReportsRemaining == 1) "" else "s"} remaining."
        needsPayment ->
            "Get another report for $price."
        else -> null
    }
}

fun BillingEntitlement.totalAvailableReports(): Int = freeReportsRemaining + paidCredits

fun BillingEntitlement.creditsSummaryLine(): String {
    val total = totalAvailableReports()
    return when {
        total == 0 && needsPayment -> "No report credits — buy one in Settings"
        total == 0 -> "No report credits remaining"
        total == 1 -> "1 report credit available"
        else -> "$total report credits available"
    }
}

fun BillingEntitlement.buyReportMessage(): String {
    val price = normalizePriceLabel(priceLabel)
    val total = totalAvailableReports()
    return when {
        total > 0 ->
            "You have $total report credit${if (total == 1) "" else "s"}. " +
                "Buy another for $price to use whenever you're ready."
        needsPayment ->
            paywallMessageForEntitlement(price)
        else ->
            "Buy a report credit for $price."
    }
}

private fun paywallMessageForEntitlement(price: String): String =
    "You've used your free report. Get another spending story for $price."
